import { useEffect, useId, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import Downshift, { type StateChangeOptions } from "downshift";
import { FaSearch } from "react-icons/fa";

import styles from "../PersonaSelector/PersonaSelector.module.scss";
import { PersonaListItem } from "components/PersonaSelector/PersonaListItem";
import type { Persona } from "types/Persona";

interface PersonaSelectorProps {
  /**
   * List of personas to choose from.
   */
  personas: Persona[];
  /**
   * The currently selected persona.
   */
  selectedPersona: Persona;
  /**
   * A callback function called when the selected persona changes.
   */
  setSelectedPersona: (newPersona: Persona) => void;
}

const TWO_COLUMN_MENU_WIDTH_REM = 50;

function searchPersonas(personas: Persona[], query: string | null) {
  return personas.filter(
    (persona) =>
      !query ||
      persona.name.toLowerCase().includes(query.toLowerCase()) ||
      persona.author?.toLowerCase().includes(query.toLowerCase())
  );
}

function sortByName(personas: Persona[]) {
  return [...personas].sort((a, b) => a.name.localeCompare(b.name));
}

function partitionPersonas(personas: Persona[]) {
  const baseModels: Persona[] = [];
  const agents: Persona[] = [];
  for (const persona of personas) {
    if (persona.isInternal === true) {
      agents.push(persona);
    } else {
      baseModels.push(persona);
    }
  }
  return {
    baseModels: sortByName(baseModels),
    agents: sortByName(agents),
  };
}

export default function PersonaSelector({
  personas,
  selectedPersona,
  setSelectedPersona,
}: PersonaSelectorProps) {
  const [inputValue, setInputValue] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [baseCollapsed, setBaseCollapsed] = useState(false);
  const [agentsCollapsed, setAgentsCollapsed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const baseColumnId = useId();
  const agentsColumnId = useId();

  const { baseModels, agents } = useMemo(
    () => partitionPersonas(personas),
    [personas]
  );

  const filteredBase = useMemo(
    () => searchPersonas(baseModels, inputValue),
    [baseModels, inputValue]
  );
  const filteredAgents = useMemo(
    () => searchPersonas(agents, inputValue),
    [agents, inputValue]
  );

  const hasAgents = agents.length > 0;
  const baseHidden = isNarrow && baseCollapsed;
  const agentsHidden = isNarrow && agentsCollapsed;
  const visibleBase = baseHidden ? [] : filteredBase;
  const visibleAgents = !hasAgents || agentsHidden ? [] : filteredAgents;

  // Sets isNarrow when the wide menu (50rem, anchored to the container's
  // left edge) would extend past the right edge of the viewport.
  useEffect(() => {
    const measure = () => {
      const node = containerRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      // Resolve rem to px against the root font size so menuWidth matches
      // the rem-based CSS width.
      const remPx =
        parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const menuWidth = TWO_COLUMN_MENU_WIDTH_REM * remPx;
      setIsNarrow(rect.left + menuWidth > window.innerWidth);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const handleStateChange = (changes: Partial<StateChangeOptions<Persona>>) => {
    if (changes.isOpen === false) {
      setInputValue("");
    }
    if (Object.hasOwn(changes, "highlightedIndex")) {
      setHighlightedIndex(changes.highlightedIndex ?? null);
    }
  };

  const handlePersonaChange = (persona: Persona | null) => {
    if (persona) {
      setSelectedPersona(persona);
    }
    setInputValue("");
  };

  const isAgentSelection = selectedPersona.isInternal === true;
  const isDeactivatedSelection = selectedPersona.active === false;

  const toggleButtonClass = isDeactivatedSelection
    ? styles.toggleButtonDeactivated
    : isAgentSelection
    ? styles.toggleButtonAgent
    : undefined;

  return (
    <Downshift
      onStateChange={handleStateChange}
      onChange={handlePersonaChange}
      itemToString={(persona) => persona?.name ?? ""}
      inputValue={inputValue}
      highlightedIndex={highlightedIndex}
    >
      {({
        getInputProps,
        getMenuProps,
        getItemProps,
        getToggleButtonProps,
        isOpen,
        highlightedIndex,
        inputValue,
      }) => {
        // Left/right arrows jump the highlight between the base and agents
        // columns when the layout is two-column. In narrow (single-column)
        // mode the arrows move the caret in the search input as usual.
        const handleSearchKeyDown = (
          e: React.KeyboardEvent<HTMLInputElement>
        ) => {
          if (isNarrow) return;
          if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
          if (highlightedIndex === null || highlightedIndex === undefined)
            return;
          const target = e.currentTarget;
          const caretAtStart =
            target.selectionStart === 0 && target.selectionEnd === 0;
          const caretAtEnd =
            target.selectionStart === target.value.length &&
            target.selectionEnd === target.value.length;
          // Only jump columns when the caret is at the edge of the input,
          // so mid-text arrow presses still move the caret normally.
          if (e.key === "ArrowLeft" && !caretAtStart) return;
          if (e.key === "ArrowRight" && !caretAtEnd) return;
          // Downshift uses a single combined index across both columns;
          // base items occupy [0, visibleBase.length) and agents follow.
          const inBase = highlightedIndex < visibleBase.length;
          if (inBase) {
            if (visibleAgents.length > 0) {
              e.preventDefault();
              setHighlightedIndex(visibleBase.length);
            }
          } else {
            if (visibleBase.length > 0) {
              e.preventDefault();
              setHighlightedIndex(0);
            }
          }
        };

        const personaSearch = (
          <span className={styles.searchContainer}>
            <FaSearch />
            <input
              {...getInputProps({
                onKeyDown: handleSearchKeyDown,
              })}
              autoFocus
              className={styles.searchBar}
              placeholder="Search personas..."
              onChange={(e) => setInputValue(e.target.value)}
            />
          </span>
        );

        const renderItem = (persona: Persona, indexInCombined: number) => {
          const itemProps = getItemProps({
            index: indexInCombined,
            key: persona.id,
            item: persona,
          });
          const { key, ...restProps } = itemProps;
          return (
            <li
              key={key}
              {...restProps}
              className={classNames(
                styles.listItem,
                highlightedIndex === indexInCombined && styles.hovered,
                selectedPersona.id === persona.id && styles.selected
              )}
            >
              <PersonaListItem persona={persona} />
            </li>
          );
        };

        const baseColumnHeader = isNarrow ? (
          <button
            type="button"
            className={styles.columnHeaderButton}
            onClick={() => setBaseCollapsed((c) => !c)}
            aria-expanded={!baseCollapsed}
            aria-controls={baseColumnId}
          >
            <span>Base Models</span>
            <span className={styles.columnHeaderGlyph}>
              {baseCollapsed ? "+" : "−"}
            </span>
          </button>
        ) : (
          <div className={styles.columnHeader}>Base Models</div>
        );

        const agentsColumnHeader = isNarrow ? (
          <button
            type="button"
            className={styles.columnHeaderButton}
            onClick={() => setAgentsCollapsed((c) => !c)}
            aria-expanded={!agentsCollapsed}
            aria-controls={agentsColumnId}
          >
            <span>Agents</span>
            <span className={styles.columnHeaderGlyph}>
              {agentsCollapsed ? "+" : "−"}
            </span>
          </button>
        ) : (
          <div className={styles.columnHeader}>Agents</div>
        );

        const baseColumn = (
          <div className={styles.column}>
            {baseColumnHeader}
            <div id={baseColumnId} hidden={baseHidden}>
              {!baseHidden &&
                (filteredBase.length === 0 && (inputValue?.length ?? 0) > 0 ? (
                  <div className={styles.emptyColumn}>No matches</div>
                ) : (
                  <ul className={styles.list}>
                    {visibleBase.map((persona, i) => renderItem(persona, i))}
                  </ul>
                ))}
            </div>
          </div>
        );

        const agentsColumn = (
          <div className={classNames(styles.column, styles.columnAgent)}>
            {agentsColumnHeader}
            <div id={agentsColumnId} hidden={agentsHidden}>
              {!agentsHidden &&
                (filteredAgents.length === 0 &&
                (inputValue?.length ?? 0) > 0 ? (
                  <div className={styles.emptyColumn}>No matches</div>
                ) : (
                  <ul className={styles.list}>
                    {/* Offset the index so agents continue Downshift's
                        combined index after the base column. */}
                    {visibleAgents.map((persona, i) =>
                      renderItem(persona, visibleBase.length + i)
                    )}
                  </ul>
                ))}
            </div>
          </div>
        );

        return (
          <div className={styles.personaSelectContainer} ref={containerRef}>
            <button
              aria-label="toggle menu"
              type="button"
              className={classNames(styles.toggleButton, toggleButtonClass)}
              {...getToggleButtonProps()}
            >
              <span>{selectedPersona?.name}</span>
              <span className={styles.angleIcon}>
                {isOpen ? (
                  <FontAwesomeIcon icon={"angle-up"} />
                ) : (
                  <FontAwesomeIcon icon={"angle-down"} />
                )}
              </span>
            </button>
            {isOpen && (
              <div
                className={classNames(
                  styles.modal,
                  isNarrow && styles.modalNarrow
                )}
                {...getMenuProps()}
              >
                {personaSearch}
                <div
                  className={classNames(
                    styles.columnsContainer,
                    isNarrow && styles.columnsContainerNarrow
                  )}
                >
                  {baseColumn}
                  {hasAgents && agentsColumn}
                </div>
              </div>
            )}
          </div>
        );
      }}
    </Downshift>
  );
}
